import { useState, useEffect } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ServerDataTable, type PagyMeta } from '@/pages/People/_components/ServerDataTable'
import type CrmFundraisingRecurringDonation from '@/types/serializers/CrmFundraisingRecurringDonation'
import type CrmFundraisingCampaign from '@/types/serializers/CrmFundraisingCampaign'
import type CrmFundraisingPaymentMethod from '@/types/serializers/CrmFundraisingPaymentMethod'
import { recurringColumns } from './_components/RecurringColumns'
import { CreateRecurringSheet } from './_components/CreateRecurringSheet'
import { RecurringDetailSheet } from './_components/RecurringDetailSheet'
import { useSelectedRow } from '@/lib/useSelectedRow'

interface PrefillDonor {
  id: number
  type: string
  name: string
}

interface Props {
  recurring_donations: CrmFundraisingRecurringDonation[]
  pagy: PagyMeta
  search?: string
  q?: Record<string, string>
  campaigns: CrmFundraisingCampaign[]
  payment_methods: CrmFundraisingPaymentMethod[]
  prefill_donor?: PrefillDonor | null
}

export default function RecurringDonationsIndex({
  recurring_donations,
  pagy,
  search,
  q,
  campaigns,
  payment_methods,
  prefill_donor,
}: Props) {
  const [createOpen, setCreateOpen] = useState(false)
  const { selected, select, clear } = useSelectedRow('/fundraising/recurring_donations')
  const selectedId = selected ? Number(selected.id) : null

  // Auto-open the create sheet when the server resolved a prefill donor from
  // ?prefill_donor_type=…&prefill_donor_id=… (deep-link CTA from the donor
  // donations tab).
  useEffect(() => {
    if (prefill_donor) {
      setCreateOpen(true)
    }
  }, [prefill_donor])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dons recurrents</h1>
          <p className="text-muted-foreground">
            Gestion des dons recurrents et suivi des versements.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nouveau don recurrent
        </Button>
      </div>

      <ServerDataTable
        columns={recurringColumns}
        data={recurring_donations}
        pagy={pagy}
        basePath="/fundraising/recurring_donations"
        selectedId={selected?.id ?? null}
        onRowClick={(row) => select(row.id)}
      />

      <RecurringDetailSheet
        recurringDonationId={selectedId}
        onOpenChange={(open) => { if (!open) clear() }}
      />

      <CreateRecurringSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        campaigns={campaigns ?? []}
        paymentMethods={payment_methods ?? []}
        defaultDonor={prefill_donor ?? undefined}
        lockDonor={Boolean(prefill_donor)}
      />
    </div>
  )
}
